import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { 
  Heart, Users, Clock, Star, TrendingUp, ShieldCheck, 
  ArrowRight, CheckCircle2, Building, Zap, Calendar,
  Phone, MessageSquare, BarChart3, Sparkles
} from 'lucide-react';

const PrivatePracticePage = () => {
  return (
    <div className="min-h-screen bg-white font-sans text-gray-900">
      <Header />

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 relative overflow-hidden bg-slate-900 text-white">
        <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-10"></div>
        <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-blue-600/20 rounded-full blur-[120px] -translate-y-1/2 translate-x-1/2 pointer-events-none"></div>
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-indigo-600/20 rounded-full blur-[100px] translate-y-1/2 -translate-x-1/2 pointer-events-none"></div>

        <div className="container mx-auto max-w-7xl relative z-10 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-500/10 border border-blue-400/20 text-blue-300 text-xs font-bold uppercase tracking-wider mb-6">
            <Heart className="w-4 h-4" />
            Independent Practice Solutions
          </div>
          <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
            Compete with DSO Efficiency, <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-400">
              Keep Your Independence
            </span>
          </h1>
          <p className="text-lg text-slate-300 max-w-2xl mx-auto mb-10 leading-relaxed">
            Get the automation and operational advantages large groups enjoy, without the corporate bureaucracy. Your small practice can operate with enterprise-level efficiency while preserving the personal touch that defines private practice.
          </p>
          
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/book-demo">
              <button className="px-8 py-4 rounded-xl font-bold text-base transition-all bg-blue-600 text-white hover:bg-blue-700 shadow-lg shadow-blue-500/30 transform hover:-translate-y-1">
                Book Your Free Demo
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* Key Value Props */}
      <section className="py-24 px-6 bg-white">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center max-w-3xl mx-auto mb-20">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Why Independent Practices Choose Asha
            </h2>
            <p className="text-lg text-gray-600">
              Built for your reality: limited staff, budget-conscious decisions, and deep patient relationships. Asha respects these realities rather than ignoring them.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {/* Feature 1 */}
            <div className="group p-8 rounded-3xl bg-gray-50 border border-gray-100 hover:border-blue-200 hover:shadow-xl hover:shadow-blue-900/5 transition-all duration-300">
              <div className="w-14 h-14 rounded-2xl bg-blue-100 text-blue-600 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Zap className="w-7 h-7" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">DSO-Level Efficiency</h3>
              <p className="text-gray-600 leading-relaxed">
                Get the same automation advantages large groups have, without losing your independence or personal touch. Your small team performs like a larger organization.
              </p>
            </div>

            {/* Feature 2 */}
            <div className="group p-8 rounded-3xl bg-gray-50 border border-gray-100 hover:border-indigo-200 hover:shadow-xl hover:shadow-indigo-900/5 transition-all duration-300">
              <div className="w-14 h-14 rounded-2xl bg-indigo-100 text-indigo-600 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Sparkles className="w-7 h-7" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Flexible & Affordable</h3>
              <p className="text-gray-600 leading-relaxed">
                Start with what you need, add features as you grow. No enterprise minimums, no paying for capabilities you're not ready for. Month-to-month flexibility.
              </p>
            </div>

            {/* Feature 3 */}
            <div className="group p-8 rounded-3xl bg-gray-50 border border-gray-100 hover:border-green-200 hover:shadow-xl hover:shadow-green-900/5 transition-all duration-300">
              <div className="w-14 h-14 rounded-2xl bg-green-100 text-green-600 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Heart className="w-7 h-7" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Enhance, Don't Replace</h3>
              <p className="text-gray-600 leading-relaxed">
                Amplify the personal touch that defines private practice. Your patients still get the relationships they value, but nothing falls through the cracks.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Never Miss an Opportunity Section */}
      <section className="py-24 px-6 bg-gradient-to-b from-white to-blue-50/30">
        <div className="container mx-auto max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-gray-200 text-blue-600 text-xs font-bold uppercase tracking-wider mb-6 shadow-sm">
                <Calendar className="w-4 h-4" />
                Schedule Optimization
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 leading-tight">
                Never Miss an Opportunity <br />
                <span className="text-blue-600">to Fill Your Schedule</span>
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Empty chairs are lost revenue you can never recover. Most private practices leave thousands of dollars on the table each month because small teams can't keep up with the volume of outreach needed.
              </p>
              
              <ul className="space-y-4">
                {[
                  "24/7 inbound call handling—capture after-hours opportunities",
                  "Automated recall & reactivation—bring overdue patients back",
                  "Instant cancellation filling—turn gaps into production",
                  "Smart confirmations—reduce no-shows by 40%+"
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-green-500 flex-shrink-0" />
                    <span className="text-gray-700 font-medium">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="relative">
              {/* Visual: Schedule Filling */}
              <div className="bg-white rounded-[2.5rem] p-8 shadow-2xl border border-gray-100 relative overflow-hidden">
                <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-blue-500 via-indigo-500 to-blue-500"></div>
                
                {/* Schedule View */}
                <div className="mb-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Today's Schedule</h3>
                  <div className="space-y-2">
                    {/* Filled Slot */}
                    <div className="bg-green-50 border border-green-200 rounded-xl p-3 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        <span className="text-sm font-bold text-gray-900">9:00 AM - Filled</span>
                      </div>
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                    </div>
                    
                    {/* Cancellation Alert */}
                    <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-center justify-between animate-pulse">
                      <div className="flex items-center gap-3">
                        <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                        <span className="text-sm font-bold text-gray-900">2:00 PM - Cancelled</span>
                      </div>
                      <MessageSquare className="w-4 h-4 text-red-500" />
                    </div>
                    
                    {/* Auto-Filled */}
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                        <span className="text-sm font-bold text-gray-900">2:00 PM - Filled by Leena</span>
                      </div>
                      <Sparkles className="w-4 h-4 text-blue-500" />
                    </div>
                    
                    {/* Filled Slot */}
                    <div className="bg-green-50 border border-green-200 rounded-xl p-3 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        <span className="text-sm font-bold text-gray-900">4:00 PM - Filled</span>
                      </div>
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                    </div>
                  </div>
                </div>
                
                {/* Active Agent Status */}
                <div className="bg-slate-50 rounded-xl p-4 border border-slate-100 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <img src="/assets/leena.png" className="w-8 h-8 rounded-full border-2 border-white" alt="Leena" />
                    <div>
                      <p className="text-xs font-bold text-gray-900">Leena is filling gaps</p>
                      <p className="text-[10px] text-gray-500">3 slots filled this week</p>
                    </div>
                  </div>
                  <div className="h-2 w-2 rounded-full bg-green-500 animate-pulse"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Streamline Operations Section */}
      <section className="py-24 px-6 bg-white">
        <div className="container mx-auto max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="relative order-2 lg:order-1">
              {/* Visual: Task Management */}
              <div className="bg-white rounded-[2.5rem] p-8 shadow-2xl border border-gray-100 relative overflow-hidden">
                <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-indigo-500 via-purple-500 to-indigo-500"></div>
                
                <div className="mb-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Today's Tasks</h3>
                  <div className="space-y-2">
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Phone className="w-4 h-4 text-blue-600" />
                        <span className="text-sm font-medium text-gray-900">Call Sarah J. - Follow-up</span>
                      </div>
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                    </div>
                    
                    <div className="bg-purple-50 border border-purple-200 rounded-xl p-3 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <MessageSquare className="w-4 h-4 text-purple-600" />
                        <span className="text-sm font-medium text-gray-900">Verify Insurance - 5 patients</span>
                      </div>
                      <div className="text-xs font-bold text-purple-600">Auto-complete</div>
                    </div>
                    
                    <div className="bg-green-50 border border-green-200 rounded-xl p-3 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Calendar className="w-4 h-4 text-green-600" />
                        <span className="text-sm font-medium text-gray-900">Confirm Tomorrow's Appts</span>
                      </div>
                      <div className="text-xs font-bold text-green-600">Done</div>
                    </div>
                  </div>
                </div>
                
                <div className="bg-slate-50 rounded-xl p-3 border border-slate-100">
                  <p className="text-xs text-gray-600 text-center">
                    <span className="font-bold text-gray-900">12 hours</span> saved this week by AI agents
                  </p>
                </div>
              </div>
            </div>
            
            <div className="order-1 lg:order-2">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-gray-200 text-indigo-600 text-xs font-bold uppercase tracking-wider mb-6 shadow-sm">
                <Users className="w-4 h-4" />
                Team Efficiency
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 leading-tight">
                Streamline Operations So Your Team <br />
                <span className="text-indigo-600">Can Focus on Patients</span>
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Small teams create small practices' biggest challenge: there's always more administrative work than hours in the day. Tasks fall through cracks, follow-ups get forgotten.
              </p>
              
              <ul className="space-y-4">
                {[
                  "Centralized task management—eliminate sticky notes and forgotten follow-ups",
                  "Intelligent opportunity capture—never miss treatment opportunities",
                  "Emergency routing—urgent needs get instant attention",
                  "Your small team performs like a larger organization"
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-green-500 flex-shrink-0" />
                    <span className="text-gray-700 font-medium">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Build Reputation Section */}
      <section className="py-24 px-6 bg-slate-50 border-y border-gray-200">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-gray-200 text-green-600 text-xs font-bold uppercase tracking-wider mb-6 shadow-sm">
              <Star className="w-4 h-4" />
              Reputation Management
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Build Your Reputation Automatically
            </h2>
            <p className="text-lg text-gray-600">
              Online reviews drive new patient decisions, but most private practices struggle with reputation management. Small teams don't have dedicated marketing staff.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white p-8 rounded-2xl border border-gray-100 shadow-sm">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mb-6 text-green-600">
                <MessageSquare className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Automated Feedback Collection</h3>
              <p className="text-gray-600 leading-relaxed">
                Collect patient feedback after every visit without staff having to remember. Know what patients think while experiences are fresh.
              </p>
            </div>

            <div className="bg-white p-8 rounded-2xl border border-gray-100 shadow-sm">
              <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center mb-6 text-yellow-600">
                <Star className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Perfect-Timing Review Requests</h3>
              <p className="text-gray-600 leading-relaxed">
                Prompt happy patients to leave reviews at the moment they're most satisfied. Grow your Google rating without staff effort.
              </p>
            </div>

            <div className="bg-white p-8 rounded-2xl border border-gray-100 shadow-sm">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-6 text-blue-600">
                <TrendingUp className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Grow Your Online Presence</h3>
              <p className="text-gray-600 leading-relaxed">
                Compete with DSO marketing presence in local search results. Build the online reputation needed to attract new patients.
              </p>
            </div>

            <div className="bg-white p-8 rounded-2xl border border-gray-100 shadow-sm">
              <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mb-6 text-red-600">
                <ShieldCheck className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Issue Identification</h3>
              <p className="text-gray-600 leading-relaxed">
                Catch problems before they become negative reviews. Address concerns privately before dissatisfied patients take frustrations public.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Insights Section */}
      <section className="py-24 px-6 bg-white">
        <div className="container mx-auto max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-gray-200 text-purple-600 text-xs font-bold uppercase tracking-wider mb-6 shadow-sm">
                <BarChart3 className="w-4 h-4" />
                Practice Insights
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 leading-tight">
                Gain Insights Into Your <br />
                <span className="text-purple-600">Practice Performance</span>
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                You can't improve what you don't measure. Most private practices lack the analytics capabilities that DSOs use to optimize performance.
              </p>
              
              <ul className="space-y-4">
                {[
                  "Real-time performance dashboards—track metrics as they happen",
                  "Staff performance & training insights—identify coaching opportunities",
                  "Operational metrics—monitor recall effectiveness and fill rates",
                  "ROI tracking—make data-driven decisions, not gut-feel guesses"
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-green-500 flex-shrink-0" />
                    <span className="text-gray-700 font-medium">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="relative">
              {/* Visual: Dashboard Preview */}
              <div className="bg-slate-900 rounded-[2.5rem] p-8 shadow-2xl border border-slate-700 relative overflow-hidden">
                <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-purple-500 via-pink-500 to-purple-500"></div>
                
                <div className="mb-6">
                  <h3 className="text-lg font-bold text-white mb-4">Practice Dashboard</h3>
                  
                  {/* Stats Grid */}
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
                      <div className="text-xs text-slate-400 mb-1">Call Volume</div>
                      <div className="text-2xl font-bold text-white">847</div>
                      <div className="text-xs text-green-400">↑ 23%</div>
                    </div>
                    <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
                      <div className="text-xs text-slate-400 mb-1">Fill Rate</div>
                      <div className="text-2xl font-bold text-white">94%</div>
                      <div className="text-xs text-green-400">↑ 8%</div>
                    </div>
                    <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
                      <div className="text-xs text-slate-400 mb-1">Avg Rating</div>
                      <div className="text-2xl font-bold text-white">4.9</div>
                      <div className="text-xs text-green-400">↑ 0.3</div>
                    </div>
                    <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
                      <div className="text-xs text-slate-400 mb-1">Revenue</div>
                      <div className="text-2xl font-bold text-white">$82K</div>
                      <div className="text-xs text-green-400">↑ 15%</div>
                    </div>
                  </div>
                </div>
                
                <div className="bg-slate-800 rounded-xl p-3 border border-slate-700">
                  <p className="text-xs text-slate-400 text-center">
                    Real-time insights • Updated every minute
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features That Matter Section */}
      <section className="py-24 px-6 bg-gradient-to-b from-white to-blue-50/30">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Private Practice Features That Matter
            </h2>
            <p className="text-lg text-gray-600">
              Everything you need to compete with larger groups, designed specifically for independent practices.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Zap, title: "Quick Implementation", desc: "Up and running in days, not months, with minimal disruption" },
              { icon: Heart, title: "Sounds Like Your Practice", desc: "Fully customizable voice and messaging to match your brand" },
              { icon: ShieldCheck, title: "HIPAA-Compliant", desc: "Enterprise-level security for patient data protection" },
              { icon: Building, title: "Seamless PMS Migration", desc: "Move from Dentrix, Open Dental, Curve, or Eaglesoft onto Asha without a messy rollout" },
              { icon: Users, title: "White-Glove Support", desc: "Dedicated support team treats you like their only client" },
              { icon: Calendar, title: "No Long-Term Contracts", desc: "Month-to-month flexibility respects your autonomy" }
            ].map((feature, i) => {
              const IconComponent = feature.icon;
              return (
                <div key={i} className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                  <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center mb-4 text-blue-600">
                    <IconComponent className="w-5 h-5" />
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2">{feature.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{feature.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6">
        <div className="container mx-auto max-w-5xl">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-700 rounded-[2.5rem] p-12 text-center text-white shadow-2xl relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl transform translate-x-1/2 -translate-y-1/2"></div>
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-black/10 rounded-full blur-3xl transform -translate-x-1/2 translate-y-1/2"></div>
            
            <h2 className="text-3xl md:text-5xl font-bold mb-6 relative z-10">
              Ready to Compete with DSO Efficiency?
            </h2>
            <p className="text-blue-100 text-lg mb-10 max-w-2xl mx-auto relative z-10">
              Stop competing with one hand tied behind your back. Get the operational advantages large groups enjoy while preserving the independence and patient relationships that make private practice rewarding.
            </p>
            
            <div className="flex flex-col sm:flex-row justify-center gap-4 relative z-10">
              <Link to="/book-demo">
                <button className="px-8 py-4 bg-white text-blue-600 rounded-xl font-bold text-lg hover:bg-blue-50 transition-colors shadow-lg">
                  Book Your Free Demo
                </button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PrivatePracticePage;
